import React, { useEffect, useMemo } from "react";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const formatDate = (value) => {
  if (!value) return "Pending";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Pending";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalize = (value) => String(value || "").toLowerCase().trim();

const PORT_COORDS = {
  shanghai: [31.2304, 121.4737],
  ningbo: [29.8683, 121.544],
  shenzhen: [22.5431, 114.0579],
  qingdao: [36.0671, 120.3826],
  tianjin: [39.0842, 117.2009],
  guangzhou: [23.1291, 113.2644],
  xiamen: [24.4798, 118.0894],
  dalian: [38.914, 121.6147],
  london: [51.5074, -0.1278],
  felixstowe: [51.9636, 1.3513],
  southampton: [50.9097, -1.4044],
  liverpool: [53.4084, -2.9916],
  dover: [51.129, 1.313],
  immingham: [53.6294, -0.1915],
  tilbury: [51.4543, 0.3604],
  glasgow: [55.8642, -4.2518],
  manchester: [53.4808, -2.2426],
  birmingham: [52.4862, -1.8904],
};

const getCoordByText = (text, country) => {
  const value = normalize(text);

  if (country === "china") {
    if (value.includes("shanghai")) return PORT_COORDS.shanghai;
    if (value.includes("ningbo")) return PORT_COORDS.ningbo;
    if (value.includes("shenzhen")) return PORT_COORDS.shenzhen;
    if (value.includes("qingdao")) return PORT_COORDS.qingdao;
    if (value.includes("tianjin")) return PORT_COORDS.tianjin;
    if (value.includes("guangzhou") || value.includes("canton")) return PORT_COORDS.guangzhou;
    if (value.includes("xiamen")) return PORT_COORDS.xiamen;
    if (value.includes("dalian")) return PORT_COORDS.dalian;
    return PORT_COORDS.shanghai;
  }

  if (country === "uk") {
    if (value.includes("felixstowe")) return PORT_COORDS.felixstowe;
    if (value.includes("southampton")) return PORT_COORDS.southampton;
    if (value.includes("liverpool")) return PORT_COORDS.liverpool;
    if (value.includes("dover")) return PORT_COORDS.dover;
    if (value.includes("immingham")) return PORT_COORDS.immingham;
    if (value.includes("tilbury")) return PORT_COORDS.tilbury;
    if (value.includes("glasgow")) return PORT_COORDS.glasgow;
    if (value.includes("manchester")) return PORT_COORDS.manchester;
    if (value.includes("birmingham")) return PORT_COORDS.birmingham;
    if (value.includes("london")) return PORT_COORDS.london;
    return PORT_COORDS.london;
  }

  return null;
};

const buildSeaRoute = (shipment) => {
  const originCoord = getCoordByText(shipment.origin, "china") || PORT_COORDS.shanghai;
  const destinationCoord = getCoordByText(shipment.destination, "uk") || PORT_COORDS.london;

  const points = [
    originCoord,
    [1.3521, 103.8198],
    [6.9271, 79.8612],
    [29.9667, 32.5498],
    [36.1408, -5.3536],
    destinationCoord,
  ];

  const stops = [
    { name: shipment.origin || "Origin Port", coords: originCoord },
    { name: "Singapore Strait", coords: [1.3521, 103.8198] },
    { name: "Colombo", coords: [6.9271, 79.8612] },
    { name: "Suez Canal", coords: [29.9667, 32.5498] },
    { name: "Gibraltar", coords: [36.1408, -5.3536] },
    { name: shipment.destination || "UK Destination", coords: destinationCoord },
  ];

  return { points, stops };
};

const interpolatePoint = (points, progress) => {
  if (!Array.isArray(points) || points.length === 0) return [51.5074, -0.1278];
  if (points.length === 1) return points[0];

  const clamped = Math.max(0, Math.min(100, Number(progress || 0)));
  const totalSegments = points.length - 1;
  const exact = (clamped / 100) * totalSegments;
  const index = Math.min(totalSegments - 1, Math.floor(exact));
  const localT = exact - index;

  const [aLat, aLng] = points[index];
  const [bLat, bLng] = points[index + 1];

  return [aLat + (bLat - aLat) * localT, aLng + (bLng - aLng) * localT];
};

function FitBounds({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds.pad(0.2));
  }, [map, points]);

  return null;
}

const markerStyle = (fill) => ({
  radius: 8,
  color: "#ffffff",
  weight: 3,
  fillColor: fill,
  fillOpacity: 1,
});

export default function ContainerJourneyMap({ shipment }) {
  const route = useMemo(() => {
    if (!shipment) {
      return {
        points: [[31.2304, 121.4737], [51.5074, -0.1278]],
        stops: [],
      };
    }

    const fallback = buildSeaRoute(shipment);

    return {
      points:
        Array.isArray(shipment.routePoints) && shipment.routePoints.length > 1
          ? shipment.routePoints
          : fallback.points,
      stops:
        Array.isArray(shipment.routeStops) && shipment.routeStops.length > 0
          ? shipment.routeStops
          : fallback.stops,
    };
  }, [shipment]);

  const currentPoint = interpolatePoint(route.points, shipment?.routeProgress || 0);

  if (!shipment) {
    return (
      <div className="grid h-[560px] place-items-center rounded-3xl border border-dashed border-slate-300 bg-white text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
        No map data available.
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
      <MapContainer
        center={route.points[0]}
        zoom={4}
        scrollWheelZoom={false}
        style={{ height: "560px", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        <FitBounds points={route.points} />

        <Polyline
          positions={route.points}
          pathOptions={{
            color: "#2563eb",
            weight: 5,
            opacity: 0.85,
            dashArray: "10, 10",
          }}
        />

        {route.stops.map((stop, index) => (
          <CircleMarker
            key={`${stop.name}-${index}`}
            center={stop.coords}
            {...markerStyle(
              index === 0 ? "#0f766e" : index === route.stops.length - 1 ? "#16a34a" : "#64748b"
            )}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">{stop.name}</div>
                <div>
                  {index === 0
                    ? "Origin"
                    : index === route.stops.length - 1
                    ? "Destination"
                    : "Route stop"}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}

        <CircleMarker center={currentPoint} {...markerStyle("#f59e0b")}>
          <Popup>
            <div className="text-sm">
              <div className="font-semibold">Current Position</div>
              <div>{shipment.routeState || shipment.status || "In Transit"}</div>
            </div>
          </Popup>
        </CircleMarker>
      </MapContainer>

      <div className="absolute left-3 bottom-3 z-[500] w-[280px] rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-lg backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Live Tracking
          </p>
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            {shipment.routeState || "In Transit"}
          </span>
        </div>

        <p className="mt-1 text-sm font-bold text-slate-900 dark:text-slate-100">
          {shipment.containerNumber || "-"}
        </p>

        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
          <div>
            <p className="text-slate-400">Origin</p>
            <p className="font-medium text-slate-800 dark:text-slate-200">
              {shipment.origin || "China"}
            </p>
          </div>
          <div>
            <p className="text-slate-400">Destination</p>
            <p className="font-medium text-slate-800 dark:text-slate-200">
              {shipment.destination || "United Kingdom"}
            </p>
          </div>
        </div>

        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <span>Route Progress</span>
            <span>{Number(shipment.routeProgress || 0)}%</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-blue-500"
              style={{ width: `${Number(shipment.routeProgress || 0)}%` }}
            />
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
          <span>{formatDate(shipment.date)}</span>
          <span>{formatDate(shipment.deliveryBookedOn || shipment.expectedArrival)}</span>
        </div>
      </div>
    </div>
  );
}